import { useState } from 'react';
import { useSettings, StoreDetails } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader, AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { LogoUpload } from '@/components/ui/logo-upload';
import { CURRENCY_OPTIONS } from '@/utils/currency';

// Constants
const BUSINESS_TYPES = [
  { id: "restaurant", label: "Restaurant & Cafe", icon: "🍽️" },
  { id: "salon", label: "Salon & Beauty", icon: "💇‍♀️" },
  { id: "retail", label: "Retail Store", icon: "🏪" },
  { id: "fashion", label: "Fashion & Apparel", icon: "👕" },
  { id: "food", label: "Home-Based Food", icon: "🥘" },
  { id: "bakery", label: "Bakery & Pastry", icon: "🥖" },
  { id: "jewelry", label: "Jewelry & Accessories", icon: "💍" },
  { id: "pharmacy", label: "Pharmacy & Health", icon: "💊" },
  { id: "digital", label: "Digital Products", icon: "💻" },
  { id: "hospitality", label: "Hospitality", icon: "🏨" },
  { id: "car_dealer", label: "Car Dealer", icon: "🚗" },
  { id: "other", label: "Other", icon: "💡" },
  { id: "services", label: "Services", icon: "📅" }
];

export function StoreDetailsSettings() {
  const { storeDetails, isUpdating, updateStoreDetails } = useSettings();
  const [phoneError, setPhoneError] = useState<string>('');
  const [showUrlWarning, setShowUrlWarning] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [formError, setFormError] = useState<string>('');
  
  const [formData, setFormData] = useState<StoreDetails>({
    ...storeDetails,
    address: storeDetails.address || {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    },
    socialLinks: storeDetails.socialLinks || {
      facebook: '',
      instagram: '',
      twitter: '',
      tiktok: '',
      youtube: '',
      website: '',
    },
    businessHours: storeDetails.businessHours || [
      { day: 'Monday', open: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'Tuesday', open: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'Wednesday', open: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'Thursday', open: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'Friday', open: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'Saturday', open: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'Sunday', open: false, openTime: '09:00', closeTime: '17:00' },
    ],
    seo: storeDetails.seo || {
      title: '',
      description: '',
      keywords: '',
      ogImage: '',
    },
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Show warning if url is being changed
    if (name === 'url' && value !== storeDetails.url) {
      setShowUrlWarning(true);
    } else if (name === 'url' && value === storeDetails.url) {
      setShowUrlWarning(false);
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleBusinessTypeChange = (value: string) => {
    setFormData(prev => ({ ...prev, businessType: value }));
  };
  
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address!,
        [name]: value,
      },
    }));
  };
  
  const handleSocialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks!,
        [name]: value,
      },
    }));
  };
  
  const handleSeoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      seo: {
        ...prev.seo!,
        [name]: value,
      },
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setPhoneError('');
    setFormError('');
    
    // Validate phone number
    if (formData.whatsappNumber && !isValidPhoneNumber(formData.whatsappNumber)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    
    try {
      // Include the logo in the update if it's been changed
      const updatedData = {
        ...formData,
        ...(logoBase64 ? { logo: logoBase64 } : {})
      };
      
      await updateStoreDetails(updatedData);
    } catch (error: unknown) {
      console.error('Error updating store details:', error);
    }
  };
  
  return (
    <div className="space-y-8">
      {formError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <section>
          <p className="text-base font-medium mb-3 border-b border-dashed border-gray-300 pb-1">Basic Details</p>
          <div className="space-y-2">
            <div className="space-y-2">
              <Label htmlFor="storeLogo">Store Logo</Label>
              <LogoUpload 
                value={logoFile}
                onChange={(file) => {
                  setLogoFile(file);
                  // Clear base64 when file is null
                  if (!file) {
                    setLogoBase64(null);
                  }
                }}
                initialPreview={logoBase64 || storeDetails.logo || undefined}
                onProcessed={(base64Data) => {
                  // Check if the base64 data is too large
                  const sizeInBytes = Math.ceil((base64Data.length - 22) * 0.75);
                  if (sizeInBytes > 90000) { // 90KB safety threshold
                    setFormError('Logo size exceeds the limit. The system automatically compressed it, but it\'s still too large. Please use a simpler image.');
                    setLogoBase64(null);
                    setLogoFile(null);
                  } else {
                    setFormError('');
                    setLogoBase64(base64Data);
                  }
                }}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="storeName">Store Name</Label>
              <Input
                id="storeName"
                name="storeName"
                value={formData.storeName}
                onChange={handleChange}
                className="!h-9 border-gray-300 text-[12px] font-light"
                placeholder="Your store name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="url">Store URL</Label>
              <div className="flex">
                <div className="flex-shrink-0 items-center flex px-3 bg-muted rounded-l-md border border-r-0 !h-9 border-gray-300 text-[12px] font-light">
                  https://sqale.shop/
                </div>
                <Input
                  id="url"
                  name="url"
                  className="rounded-l-none !h-9 border-gray-300 text-[12px] font-light"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="your-store-url"
                />
              </div>
              {showUrlWarning && (
                <Alert variant="warning" className="mt-2 !border-red-700">
                  <AlertTriangle className="h-4 w-4 !text-red-700" />
                  <AlertDescription className="!text-red-700">
                    Link to your store. Changing this will break your existing QR code and shared links.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Your business name"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="businessType">Business Type</Label>
              <Select
                value={formData.businessType}
                onValueChange={handleBusinessTypeChange}
              >
                <SelectTrigger id="businessType" className="w-full !h-9 border-gray-300 text-[12px] font-light">
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="inline-flex items-center gap-2 text-[12px] font-light">
                        <span>{type.icon}</span>
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            
            <div className="space-y-2">
              <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
              <PhoneInput
                international
                defaultCountry="NG"
                value={formData.whatsappNumber}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, whatsappNumber: value || '' }));
                  setPhoneError('');
                }}
                placeholder="Enter your WhatsApp number"
                error={phoneError}
                size="sm"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
              {phoneError && (
                <p className="text-sm text-destructive">{phoneError}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
              >
                <SelectTrigger id="currency" className="w-full !h-9 border-gray-300 text-[12px] font-light">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((currency) => (
                    <SelectItem key={currency.id} value={currency.id} >
                      <span className="text-[12px] font-light">{currency.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section>
          <p className="text-base font-medium mb-3 mt-4 border-b border-dashed border-gray-300 pb-1">Address</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="street">Street Address</Label>
              <Input
                id="street"
                name="street"
                value={formData.address?.street}
                onChange={handleAddressChange}
                placeholder="Street address"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                name="city"
                value={formData.address?.city}
                onChange={handleAddressChange}
                placeholder="City"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="state">State/Province</Label>
              <Input
                id="state"
                name="state"
                value={formData.address?.state}
                onChange={handleAddressChange}
                placeholder="State or province"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal Code</Label>
              <Input
                id="postalCode"
                name="postalCode"
                value={formData.address?.postalCode}
                onChange={handleAddressChange}
                placeholder="Postal code"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                name="country"
                value={formData.address?.country}
                onChange={handleAddressChange}
                placeholder="Country"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
          </div>
        </section>

        <section>
          <p className="text-base font-medium mb-3 mt-4 border-b border-dashed border-gray-300 pb-1">Social Links</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="facebook">Facebook</Label>
              <Input
                id="facebook"
                name="facebook"
                value={formData.socialLinks?.facebook}
                onChange={handleSocialChange}
                placeholder="Facebook page URL"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                id="instagram"
                name="instagram"
                value={formData.socialLinks?.instagram}
                onChange={handleSocialChange}
                placeholder="Instagram handle or URL"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="twitter">Twitter/X</Label>
              <Input
                id="twitter"
                name="twitter"
                value={formData.socialLinks?.twitter}
                onChange={handleSocialChange}
                placeholder="Twitter handle or URL"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tiktok">TikTok</Label>
              <Input
                id="tiktok"
                name="tiktok"
                value={formData.socialLinks?.tiktok}
                onChange={handleSocialChange}
                placeholder="TikTok handle or URL"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="youtube">YouTube</Label>
              <Input
                id="youtube"
                name="youtube"
                value={formData.socialLinks?.youtube}
                onChange={handleSocialChange}
                placeholder="YouTube channel URL"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                value={formData.socialLinks?.website}
                onChange={handleSocialChange}
                placeholder="Your website URL"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
            </div>
          </div>
        </section>

        <section>
          <p className="text-base font-medium mb-3 mt-4 border-b border-dashed border-gray-300 pb-1">SEO</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="seoTitle">Meta Title</Label>
              <Input
                id="seoTitle"
                name="title"
                value={formData.seo?.title || ''}
                onChange={handleSeoChange}
                placeholder="Meta title for search engines"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be displayed as the page title in search engines. If left empty, your store name will be used.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="seoDescription">Meta Description</Label>
              <Textarea
                id="seoDescription"
                name="description"
                value={formData.seo?.description || ''}
                onChange={handleSeoChange}
                placeholder="Brief description for search engines"
                rows={3}
                className="border-gray-300 text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A short description of your store that will appear in search results (150-160 characters recommended).
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="seoKeywords">Keywords</Label>
              <Input
                id="seoKeywords"
                name="keywords"
                value={formData.seo?.keywords || ''}
                onChange={handleSeoChange}
                placeholder="e.g. restaurant, lagos, food delivery"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated keywords relevant to your business. These help potential customers find you.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ogImage">Social Share Image URL</Label>
              <Input
                id="ogImage"
                name="ogImage"
                value={formData.seo?.ogImage || ''}
                onChange={handleSeoChange}
                placeholder="https://example.com/your-image.jpg"
                className="!h-9 border-gray-300 text-[12px] font-light"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Image displayed when your store is shared on social media. If empty, your store logo will be used.
              </p>
            </div>
          </div>
        </section>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isUpdating} size="sm">
            {isUpdating ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
} 